import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

const LoadingScreen = ({ onFinish }) => {
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const onFinishRef = React.useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    const start = Date.now();
    const duration = 600;
    let raf;

    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);

      if (pct < 100) {
        raf = requestAnimationFrame(tick);
      } else {
        setTimeout(() => setFadeOut(true), 100);
        setTimeout(() => onFinishRef.current(), 400);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#E4F0F6',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: fadeOut ? 0 : 1,
        transform: fadeOut ? 'scale(1.03)' : 'scale(1)',
        overflow: 'hidden',

        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '500px',
          height: '500px',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(85, 37, 131, 0.08) 0%, transparent 70%)',
        },

        '@keyframes logoReveal': {
          '0%': { transform: 'translateY(12px) scale(0.95)', opacity: 0 },
          '100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
        },
        '@keyframes barGrow': {
          '0%': { transform: 'scaleX(0)', opacity: 0 },
          '100%': { transform: 'scaleX(1)', opacity: 1 },
        },
        '@keyframes subtitleFade': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          animation: 'logoReveal 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '20px',
            background: 'rgba(85, 37, 131, 0.1)',
            border: '2px solid rgba(85, 37, 131, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #552583 0%, #FDB927 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            U
          </Typography>
        </Box>

        {/* Name */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            color: '#552583',
            letterSpacing: '0.15em',
            fontSize: { xs: '1.6rem', sm: '2rem' },
            mb: 0.5,
          }}
        >
          USAFIRI
        </Typography>

        {/* Tagline */}
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(85, 37, 131, 0.45)',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            fontSize: '0.65rem',
            fontWeight: 600,
            animation: 'subtitleFade 0.3s ease-out 0.15s both',
          }}
        >
          La plateforme de Mayotte
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          mt: 3.5,
          width: 180,
          animation: 'barGrow 0.3s ease-out 0.1s both',
          transformOrigin: 'center',
        }}
      >
        <Box
          sx={{
            height: 2.5,
            borderRadius: 2,
            background: 'rgba(85, 37, 131, 0.1)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: 2,
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #552583, #FDB927)',
              boxShadow: '0 0 8px rgba(253, 185, 39, 0.4)',
              transition: 'width 0.05s linear',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingScreen;
